import { classNames } from "@/utility/jsx";
import { type PropsWithChildren, type ReactNode, useState } from "react";
import { EditableLayerLabel } from "../EditableLayerLabel";

interface HeaderProps {
  seriesType?: string;
  expanded: boolean;
  hideTag?: boolean;
  actionButton?: ReactNode;
  isTitleEditable?: boolean;
  onExpanedChange: (expanded: boolean) => void;
}

function Header({
  seriesType,
  expanded,
  hideTag,
  actionButton,
  isTitleEditable,
  onExpanedChange,
  children,
}: PropsWithChildren<HeaderProps>) {
  const toggle = () => onExpanedChange(!expanded);

  return (
    <div className={"expansion-panel__header"}>
      <div
        className={classNames(
          "expansion-panel__header__layer-info",
          hideTag ? "expansion-panel__header__layer-info__sub" : "",
        )}
        onClick={toggle}
      >
        <span className="expansion-panel__chevron">
          <span className="material-icons">{expanded ? "expand_less" : "expand_more"}</span>
        </span>
        <span className="layer-label">
          {!hideTag && <span className="layer-label__kindName tag">{seriesType} </span>}
          <span
            className={classNames(
              "layer-label__kindName layer-label__kindName--parameter",
              isTitleEditable && "layer-label__kindName--editable",
            )}
          >
            {children}
          </span>
        </span>
      </div>
      {actionButton && (
        <div className={`expansion-panel__header__right-control ${expanded ? "" : ""}`}>{actionButton}</div>
      )}
    </div>
  );
}

interface SectionProps {
  tag?: string;
  title?: ReactNode;
  editableTitle?: {
    defaultLabel: string;
    label?: string | null;
    onSave: (label: string | null) => void;
  };
  actionButton?: ReactNode;
  subSection?: boolean;
  defaultOpen?: boolean;
}

export function Section({
  tag,
  title,
  editableTitle,
  actionButton,
  subSection,
  defaultOpen,
  children,
}: PropsWithChildren<SectionProps>) {
  const [isOpen, setIsOpen] = useState(defaultOpen ?? false);
  const toggle = () => setIsOpen((expanded) => !expanded);

  return (
    <div className="actionable" draggable={false}>
      <div className={classNames("expansion-panel box-section--breakout", isOpen && "expansion-panel--expanded")}>
        <Header
          seriesType={tag}
          actionButton={actionButton}
          expanded={isOpen}
          hideTag={subSection}
          isTitleEditable={!!editableTitle}
          onExpanedChange={(expanded) => setIsOpen(expanded)}
        >
          {editableTitle ? (
            <EditableLayerLabel
              defaultLabel={editableTitle.defaultLabel}
              layer={{ label: editableTitle.label }}
              onSave={editableTitle.onSave}
              onSingleClick={toggle}
            />
          ) : (
            title
          )}
        </Header>
      </div>
      {isOpen && <div className="expansion-panel__content">{children}</div>}
    </div>
  );
}
